import { PageOrientation, Style, TableCell } from "pdfmake/interfaces";
import { TFunction } from "react-i18next";
import { MoneyApi } from "go-core/api/types";
import { estimatePdfTextWidth } from "go-list/core/components/Actions/services";
import {
	OrderApi,
	OrderItemApi,
	PurchaseApi,
	PurchaseItemApi,
} from "../../../../../../../../services/Api/Organization/types";
import { InvoicePaymentsPdfColumn, InvoicePdfColumn, InvoiceSummaryPdfColumn } from "../../pages/Invoice/utils/types";
import { getOrderItemNetPriceAmount } from "../../pages/Order/utils";
import { A4WIDTH, DOCUMENT_PDF_DEFAULT_COLUMNS, DOCUMENT_PDF_TABLE_STYLES } from "./consts";
import {
	DocumentPdfAdditionalColumn,
	DocumentPdfColumn,
	DocumentPdfDefaultColumn,
	DocumentPdfEntity,
	ExportDocumentPdfConfig,
} from "./types";

export const generateDocumentItemsTable = (
	config: ExportDocumentPdfConfig,
	entity: DocumentPdfEntity,
	t: TFunction,
	data: (PurchaseItemApi | OrderItemApi)[],
	document: PurchaseApi | OrderApi
) => {
	const columns = getDocumentItemsTableColumns(config, entity);
	const widths: (string | number)[] = [];
	const tHead: { style: Style; text: string }[] = [];
	const tableBody: TableCell[][] = [];

	columns.forEach((column) => {
		tHead.push({
			text: findNameForColumn(column, t, document.tax_gross),
			style: DOCUMENT_PDF_TABLE_STYLES.tableHeader,
		});
		widths.push("auto");
	});
	tableBody.push(tHead);

	const table = {
		widths,
		body: tableBody,
		dontBreakRows: true,
		pageOrientation: "portrait" as PageOrientation,
		overWidth: false,
	};
	const newWidthsArray = table.widths.map(() => 0);
	const defaultMaxWidth = ((A4WIDTH - 40) * 1.5) / columns.length;
	const columnWidths: { column: DocumentPdfColumn; width: number; minWidth: number }[] = [];

	data.forEach((item) => {
		const bodyRow: { text: { noWrap: boolean; text: string | number }; style: Style }[] = [];
		columns.forEach((column, columnIndex) => {
			bodyRow.push({
				text: { noWrap: false, text: getValueForColumn(column, item, document, entity, t) },
				style: DOCUMENT_PDF_TABLE_STYLES.tableCell,
			});

			const row = bodyRow[columnIndex];
			if (row) {
				const headLength = tHead[columnIndex]?.text.length;
				const contentLength = !row.text.text ? headLength : row.text.text.toString().length;
				const text = (headLength > contentLength ? tHead[columnIndex]?.text : row.text.text)?.toString() || "";
				if (text) {
					const width = estimatePdfTextWidth(text, headLength >= contentLength);
					if (width > defaultMaxWidth) {
						newWidthsArray[columnIndex] = defaultMaxWidth;
					} else {
						newWidthsArray[columnIndex] = Math.max(newWidthsArray[columnIndex], width);
					}

					columnWidths.push({
						column,
						minWidth:
							width > defaultMaxWidth ? defaultMaxWidth : Math.max(newWidthsArray[columnIndex], width),
						width,
					});
				}
			}
		});
		table.body.push(bodyRow);
	});

	table.widths = newWidthsArray.map((width, index) => {
		return width || table.widths[index];
	});

	const tableWidthSum: number = table.widths
		.map((width) => {
			return Number(width) || 0;
		})
		.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

	const CELL_PADDING = 7;
	const fullTableWidth = tableWidthSum + CELL_PADDING * columns.length;

	if (fullTableWidth > A4WIDTH - 40) {
		table.widths = table.widths.map((_) => {
			return "auto";
		});
	} else {
		table.widths = calculateDocumentColumnWidths(table.widths, columnWidths, flexibleColumns);
	}
	return table;
};
const flexibleColumns: DocumentPdfColumn[] = [DocumentPdfDefaultColumn.NAME];

export const calculateDocumentColumnWidths = (
	widths: (number | string)[],
	columnWidths: {
		column: DocumentPdfColumn | InvoicePdfColumn | InvoiceSummaryPdfColumn | InvoicePaymentsPdfColumn;
		width: number;
		minWidth: number;
	}[],
	flexibleColumns: (DocumentPdfColumn | InvoicePdfColumn | InvoiceSummaryPdfColumn | InvoicePaymentsPdfColumn)[]
) => {
	return widths.map((width, index) => {
		const isFlexibleColumn = flexibleColumns.includes(columnWidths[index]?.column);
		if (isFlexibleColumn) {
			return "*";
		}
		return width;
	});
};

export const getDocumentItemsTableColumns = (config: ExportDocumentPdfConfig, entity: DocumentPdfEntity) => {
	const columns: DocumentPdfColumn[] = [...DOCUMENT_PDF_DEFAULT_COLUMNS];

	if (config.printCosts && entity === DocumentPdfEntity.ORDER) {
		const indexToInsert = columns.indexOf(DocumentPdfDefaultColumn.TAX);
		columns.splice(indexToInsert, 0, DocumentPdfAdditionalColumn.UNIT_COST, DocumentPdfAdditionalColumn.COST);
	}

	if (config.printPrices) {
		let indexToInsertUnitPrice = columns.indexOf(DocumentPdfDefaultColumn.TAX);
		if (config.printCosts && entity === DocumentPdfEntity.ORDER) {
			indexToInsertUnitPrice = columns.indexOf(DocumentPdfAdditionalColumn.COST);
		}
		columns.splice(indexToInsertUnitPrice, 0, DocumentPdfAdditionalColumn.UNIT_PRICE);
		columns.push(DocumentPdfAdditionalColumn.PRICE);
	}

	return columns;
};

const getValueForColumn = (
	column: DocumentPdfColumn,
	item: PurchaseItemApi | OrderItemApi,
	document: PurchaseApi | OrderApi,
	entity: DocumentPdfEntity,
	t: TFunction
) => {
	const isTaxGross = document.tax_gross;
	const measureType = item.product?.measure_type
		? t(`enums.products.measure_types.${item.product.measure_type}`)
		: "";

	switch (column) {
		case DocumentPdfAdditionalColumn.UNIT_COST:
			if (entity === DocumentPdfEntity.ORDER && "unit_fifo_cost_net_money" in item) {
				return formatDocumentMoney(item.unit_fifo_cost_net_money);
			}
			return "";
		case DocumentPdfAdditionalColumn.COST:
			if ("total_fifo_cost_net_money" in item) {
				return formatDocumentMoney(item.total_fifo_cost_net_money);
			}
			return "";
		case DocumentPdfAdditionalColumn.PRICE:
			return isTaxGross
				? formatDocumentMoney(item.total_price_gross_money)
				: formatDocumentMoney(item.total_price_net_money);
		case DocumentPdfAdditionalColumn.UNIT_PRICE: {
			if (entity === DocumentPdfEntity.ORDER) {
				const amount = getOrderItemNetPriceAmount(item as OrderItemApi);
				if (amount === null) return "-";

				return formatDocumentMoney({
					amount,
					currency: (document as OrderApi).total_mac_cost_net_money?.currency,
				});
			}

			return isTaxGross ? formatDocumentMoney(item.price_gross_money) : formatDocumentMoney(item.price_net_money);
		}
		case DocumentPdfDefaultColumn.TAX:
			return item.tax?.name ?? "";
		case DocumentPdfDefaultColumn.QUANTITY:
			return item.quantity;
		case DocumentPdfDefaultColumn.POSITION:
			return item.position + 1;
		case DocumentPdfDefaultColumn.NAME:
			return item.product?.name ?? "";
		case DocumentPdfDefaultColumn.TOTAL_VOLUME:
			return `${(item.volume * item.quantity).toFixed(5)} ${measureType}`;
		case DocumentPdfDefaultColumn.VOLUME:
			return `${item.volume} ${measureType}`;
	}
};

const findNameForColumn = (column: DocumentPdfColumn, t: TFunction, isTaxGross: boolean) => {
	return getColumnsWithName(t, isTaxGross).find(({ id }) => id === column)?.name ?? "";
};

export const formatDocumentMoney = (value: MoneyApi) => {
	if (!value) {
		return "";
	}

	const amount = value.amount;
	if (amount === null || amount === undefined || amount?.toFixed === undefined) {
		return "-";
	}

	return `${amount.toFixed(2)} ${value.currency}`;
};

const getColumnsWithName = (t: TFunction, isTaxGross: boolean) => {
	return [
		{
			id: DocumentPdfDefaultColumn.POSITION,
			name: t("lib:go_report.word.ordinal_number_shortcut"),
		},
		{
			id: DocumentPdfDefaultColumn.NAME,
			name: t("lib:common.word.name"),
		},
		{
			id: DocumentPdfDefaultColumn.QUANTITY,
			name: t("common.word.quantity"),
		},
		{
			id: DocumentPdfDefaultColumn.VOLUME,
			name: t("common.word.volume"),
		},
		{
			id: DocumentPdfDefaultColumn.TOTAL_VOLUME,
			name: t("common.word.total_volume"),
		},
		{
			id: DocumentPdfAdditionalColumn.UNIT_PRICE,
			name: isTaxGross ? t("common.word.unit_price_gross_money") : t("common.word.unit_price_net_money"),
		},
		{
			id: DocumentPdfAdditionalColumn.PRICE,
			name: t("common.word.value"),
		},
		{
			id: DocumentPdfDefaultColumn.TAX,
			name: `${t("common.word.vat_rate")}\n${t("common.word.vat")}`,
		},
		{
			id: DocumentPdfAdditionalColumn.UNIT_COST,
			name: t("common.word.item_cost"),
		},
		{
			id: DocumentPdfAdditionalColumn.COST,
			name: t("common.word.cost"),
		},
	];
};
